import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchServices,
  fetchService,
  fetchServiceHealth,
  fetchServiceCloudAccounts,
  fetchServiceCostByEnv,
  attachCloudAccount,
  detachCloudAccount,
  createService,
  updateService,
  deleteService,
} from '@/api/services';
import type { AttachCloudAccountRequest, ServiceListParams } from '@/api/services';
import type { CreateServiceRequest, UpdateServiceRequest } from '@/types';

export function useServices(params?: ServiceListParams) {
  return useQuery({
    queryKey: ['services', params],
    queryFn: () => fetchServices(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useService(slug: string) {
  return useQuery({
    queryKey: ['service', slug],
    queryFn: () => fetchService(slug),
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });
}

export function useServiceHealth(slug: string) {
  return useQuery({
    queryKey: ['service-health', slug],
    queryFn: () => fetchServiceHealth(slug),
    staleTime: 2 * 60 * 1000,
    enabled: !!slug,
  });
}

export function useCreateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateServiceRequest) => createService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useUpdateService(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateServiceRequest) => updateService(slug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['service', slug] });
    },
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => deleteService(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useServiceCloudAccounts(slug: string) {
  return useQuery({
    queryKey: ['service-cloud-accounts', slug],
    queryFn: () => fetchServiceCloudAccounts(slug),
    staleTime: 2 * 60 * 1000,
    enabled: !!slug,
  });
}

export function useServiceCostByEnv(slug: string, periodDays = 30) {
  return useQuery({
    queryKey: ['service-cost-by-env', slug, periodDays],
    queryFn: () => fetchServiceCostByEnv(slug, periodDays),
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });
}

export function useAttachCloudAccount(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AttachCloudAccountRequest) => attachCloudAccount(slug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-cloud-accounts', slug] });
      queryClient.invalidateQueries({ queryKey: ['service', slug] });
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useDetachCloudAccount(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => detachCloudAccount(slug, accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-cloud-accounts', slug] });
      queryClient.invalidateQueries({ queryKey: ['service', slug] });
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}
